import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";

import { deleteProduct, getProduct, updateProduct, resyncProduct } from "api/Products";
import { trackActivity } from "api/ActivityTracker";

import { filterByKeys } from "helpers/default";
import { Box, Divider, Flex, Grid, Label, Link, Spinner } from "theme-ui";
import Header from "components/ui/Header";
import Page from "components/ui/Page";
import { Tab, Tabs } from "components/ui/Tabs";
import BasicButton from "components/forms/BasicButton";
import { useToast } from "components/toast";
import { BackArrowIcon, CheckMarkIcon, QuestionMarkIcon, TrashIcon } from "components/svg";
import ReloadIcon from "components/svg/icons/ReloadIcon";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import TextInput from "components/forms/TextInput";
import SelectInput from "components/forms/SelectInput";
import { themevals } from "theme/themevals";
import Separator from "components/ui/Separator";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import Checkbox from "components/forms/Checkbox";
import UserContext from "context/User";

export default function Product() {
  const navigate = useNavigate();
  const params = useParams();
  const toast = useToast();

  const [product, setProduct] = useState<any>(null);
  const { user } = useContext<any>(UserContext);
  const hasTrackedView = useRef(false);

  const submitUpdate = async (values: any) => {
    const keys = ["name", "description", "unit", "addon"];
    const update = filterByKeys(keys, values);

    const previousProduct = product;
    const res = await updateProduct({ data: { id: params.id }, update });
    if (res?.id) {
      toast.add("Product updated.", "var(--theme-ui-colors-success)");
      setProduct(res);

      if (previousProduct?.id) {
        const changedFields: string[] = [];
        if ((previousProduct.name ?? "") !== (res.name ?? "")) {
          changedFields.push("name");
        }
        if ((previousProduct.description ?? "") !== (res.description ?? "")) {
          changedFields.push("description");
        }
        if ((previousProduct.unit ?? "") !== (res.unit ?? "")) {
          changedFields.push("unit");
        }
        if ((previousProduct.addon ?? false) !== (res.addon ?? false)) {
          changedFields.push("addon");
        }

        if (changedFields.length > 0) {
          void trackActivity({
            module: "products",
            action: "update_product",
            actionType: "update",
            userId: user?.id || null,
            companyId: user?.company?.id || null,
            entityType: "product",
            entityId: res.id,
            metadata: {
              changedFields,
            },
            userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
          });
        }
      }
    } else {
      toast.add(res?.message ? res.message : "Error updating product", "var(--theme-ui-colors-danger)");
    }
  };

  const _validate = Yup.object().shape({
    name: Yup.string().required("This field is required."),
  });

  const formatLastSync = (iso?: string) => {
    if (!iso) return "N/A";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "N/A";
    const month = d.toLocaleString("en-US", { month: "short" });
    const day = d.getDate();
    const year = d.getFullYear();
    const ordinal = (n: number) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return s[(v - 20) % 10] || s[v] || s[0];
    };
    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${month} ${day}${ordinal(day)}, ${year} ${hours}:${minutes} ${ampm}`;
  };

  const initProduct = async () => {
    const res = await getProduct({ data: { id: params.id } });
    setProduct(res?.id ? res : null);
  };

  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);

  const confirmDelete = async () => {
    setConfirmSubmit(true);
    const res = await deleteProduct({ data: product });
    if (res?.success) {
      toast.add("Product deactivated.", "var(--theme-ui-colors-success)");
      void trackActivity({
        module: "products",
        action: "deactivate_product",
        actionType: "update",
        userId: user?.id || null,
        companyId: user?.company?.id || null,
        entityType: "product",
        entityId: product?.id ?? null,
        metadata: {
          source: "product_detail",
        },
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      navigate("/products");
    } else {
      toast.add(res?.message ? res.message : "Error deleting client.", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
    setConfirmModal(false);
  };

  const submitDelete = async () => {
    setConfirmModal(
      <BasicModal
        title="Deactivate product?"
        size={"sm"}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        cancel_click={() => setConfirmModal(false)}
        cancel_text="No, cancel"
        actions={
          <Flex sx={{ gap: "10px" }}>
            <BasicButton type="button" scheme="danger" onClick={() => confirmDelete()} $submitting={confirmSubmit} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
              {!confirmSubmit && <TrashIcon fill="#fff" />}
              {"Deactivate"}
            </BasicButton>
          </Flex>
        }
      >
        <P>
          Are you sure you want to deactivate <strong style={{ color: "var(--theme-ui-colors-black)" }}>{product?.name}</strong>?
        </P>
      </BasicModal>
    );
  };

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME + " | Product";
    hasTrackedView.current = false;
    initProduct();
  }, [params.id]);

  useEffect(() => {
    if (!product?.id || hasTrackedView.current) {
      return;
    }
    hasTrackedView.current = true;
    void trackActivity({
      module: "products",
      action: "view_product_detail",
      actionType: "view",
      userId: user?.id || null,
      companyId: user?.company?.id || null,
      entityType: "product",
      entityId: product.id,
      metadata: {},
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    });
  }, [product?.id, user?.id, user?.company?.id]);

  const submitSync = async () => {
    setConfirmSubmit(true);
    const res = await resyncProduct({ data: product });
    if (res?.success) {
      await initProduct();
      toast.add("Product synced.", "var(--theme-ui-colors-success)");
    } else {
      toast.add(res?.message ? res.message : "Error syncing product.", "var(--theme-ui-colors-danger)");
    }
    setConfirmSubmit(false);
  };

  const lastSyncSuccessful = product?.external?.zoho?.lastSyncSuccessful;
  const lastSyncColor =
    lastSyncSuccessful === undefined
      ? themevals.colors.dkgrey
      : lastSyncSuccessful
      ? themevals.colors.success
      : themevals.colors.danger;
  const lastSyncText = product?.external?.zoho?.lastSync
    ? formatLastSync(product.external.zoho.lastSync)
    : "N/A";

  if (!product)
    return (
      <Box sx={{ textAlign: "center", paddingY: "1.5rem" }}>
        <Spinner />
      </Box>
    );

  return (
    <>
      <Header></Header>
      <Page
        title="Edit product"
        subtitle="Edit a product of your company."
        backAction={
          <Link variant="back" href="/products">
            <BackArrowIcon />
            Back to Products
          </Link>
        }
      >
        <Formik initialValues={{ ...product }} onSubmit={submitUpdate} validationSchema={_validate} enableReinitialize>
          {({ isSubmitting, errors, values, submitCount, setFieldValue }) => (
            <Form noValidate autoComplete="off">
              <Tabs
                vertical
                stickyFooter
                footer={
                  <Flex sx={{ gap: "10px", flexGrow: 1 }}>
                    <BasicButton scheme="dangerSecondary" styles={{ marginRight: "auto!important", marginLeft: "10px" }} type="button" onClick={() => submitDelete()}>
                      <TrashIcon fill={themevals.colors.danger} /> Deactivate
                    </BasicButton>
                    <Link href="/products" sx={{ ...themevals.buttons.secondaryOutline, textDecoration: "none", margin: "0 10px 0 0" }}>
                      Cancel
                    </Link>
                    <BasicButton type="submit" $submitting={isSubmitting} styles={{ margin: "0 10px 0 0" }} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                      {!isSubmitting && <CheckMarkIcon fill={"#fff"} />}
                      Save product
                    </BasicButton>
                  </Flex>
                }
              >
                <Tab itemKey="general">
                  <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                    <Box>
                      <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                        Basic Info
                      </H5>
                      <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                        Edit main product information.
                      </Subtext>
                    </Box>
                    <Box>
                      <Flex sx={{ flexDirection: ["column", "row"], gap: "1rem", marginBottom: "1rem" }}>
                        <TextInput
                          name="name"
                          label="Name"
                          value={values.name}
                          onChange={(val: any) => setFieldValue("name", val)}
                          $errors={errors.name && submitCount > 0 ? errors.name : null}
                          $customStyles={{ flexGrow: 1, width: "100%" }}
                        />
                        <TextInput
                          name="description"
                          label="Description"
                          value={values.description}
                          onChange={(val: any) => setFieldValue("description", val)}
                          $errors={errors?.description && submitCount > 0 ? errors?.description : null}
                          $customStyles={{ flexGrow: 1, width: "100%" }}
                        />
                      </Flex>
                      <Flex sx={{ gap: "1rem", alignItems: "flex-end" }}>
                        <SelectInput
                          name="unit"
                          label="Units"
                          value={values.unit}
                          options={[
                            { value: "yds", label: "yds." },
                            { value: "sqft", label: "sqft." },
                            { value: "ea", label: "ea." },
                          ]}
                          onChange={(val: any) => setFieldValue("unit", val)}
                          $errors={errors.unit && submitCount > 0 ? errors.unit : null}
                          $customStyles={{ flexGrow: 1, width: "100%" }}
                        />
                        <Box sx={{ flexGrow: 1, width: "100%" }}>
                          <Checkbox label="Add-on product" checked={values.addon} onChange={() => setFieldValue("addon", !values.addon)} />
                        </Box>
                      </Flex>
                    </Box>
                  </Grid>
                  <Separator />
                  <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
                    <Box>
                      <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
                        Third-Party Sync
                      </H5>
                      <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                        Resync your product with your CRM provider.
                      </Subtext>
                    </Box>
                    <Box>
                      <Flex sx={{ flexDirection: "column", marginBottom: "20px" }}>
                        <Label sx={{ fontFamily: "Poppins", fontSize: 12, color: themevals.colors.dkgrey }}>Last Sync</Label>
                        <P fontFamily={"Poppins"} color={lastSyncColor} fontSize={"14px"} fontWeight={600} marginTop={"5px"} marginBottom={"0px"}>
                          {lastSyncText}
                        </P>
                      </Flex>
                      <BasicButton
                        scheme="secondaryOutline"
                        type="button"
                        $iconHeight="14px"
                        $iconWidth="14px"
                        $loadingColor="#000"
                        $submitting={confirmSubmit}
                        keepText={true}
                        onClick={() => submitSync()}
                      >
                        {!confirmSubmit && <ReloadIcon fill={"#000"} />}Resync
                      </BasicButton>
                    </Box>
                  </Grid>
                </Tab>
              </Tabs>
            </Form>
          )}
        </Formik>
      </Page>
      {confirmModal}
    </>
  );
}
